import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  XMarkIcon,
  CalendarIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

// 🎊 Confetti Particle Component
const ConfettiPiece = ({ style }) => (
  <div className="confetti-piece" style={style} />
);

const generateConfetti = (count = 40) =>
  Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    animationDelay: `${Math.random() * 0.8}s`,
    animationDuration: `${0.8 + Math.random() * 1.2}s`,
    backgroundColor: ['#6366f1', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899'][
      Math.floor(Math.random() * 7)
    ],
    width: `${6 + Math.random() * 8}px`,
    height: `${6 + Math.random() * 8}px`,
    borderRadius: Math.random() > 0.5 ? '50%' : '2px',
    transform: `rotate(${Math.random() * 360}deg)`,
  }));

const OrderConfirmationModal = ({ show, orderDetails, onOk, onClose }) => {
  const navigate = useNavigate();
  const [confetti, setConfetti] = useState([]);
  const [checkAnimated, setCheckAnimated] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setConfetti(generateConfetti(50));
      setTimeout(() => setCheckAnimated(true), 300);
      setTimeout(() => setContentVisible(true), 600);
    } else {
      setConfetti([]);
      setCheckAnimated(false);
      setContentVisible(false);
    }
  }, [show]);

  if (!show) return null;

  const handleConfirmBooking = () => {
    if (onOk) onOk();
    navigate('/service-history');
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4 overflow-hidden">

      {/* 🎊 Confetti */}
      {confetti.map((c) => (
        <ConfettiPiece
          key={c.id}
          style={{
            position: 'fixed',
            left: c.left,
            top: '-10px',
            width: c.width,
            height: c.height,
            backgroundColor: c.backgroundColor,
            borderRadius: c.borderRadius,
            transform: c.transform,
            animation: `confettiFall ${c.animationDuration} ${c.animationDelay} ease-in forwards`,
            zIndex: 60,
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Modal Card */}
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] shadow-2xl relative flex flex-col"

        style={{ animation: 'popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}
      >
        {/* ✨ Glowing Border Ring */}
        <div className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #10b981)',
            padding: '2px',
            borderRadius: '1.5rem',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          }}
        />

        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 p-6 rounded-t-3xl overflow-hidden">
          {/* Decorative Circles */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="flex justify-between items-start relative z-10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg"
                style={{ animation: 'pulse 2s infinite' }}>
                <CheckCircleIcon className="h-7 w-7 text-green-600" />
              </div>
              <div className="text-white">
                <h3 className="text-2xl font-bold tracking-tight">Order Confirmed 🎉</h3>
                <p className="text-indigo-200 text-sm">Your booking is locked in!</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-all duration-200 hover:rotate-90"
              style={{ transition: 'transform 0.2s ease' }}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto flex-1">

          {/* 🎯 Animated Checkmark Hero */}
          <div className="text-center py-6 relative">
            {/* Ripple Rings */}
            {checkAnimated && (
              <>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-36 h-36 rounded-full bg-green-100"
                    style={{ animation: 'ripple 1.5s ease-out infinite' }} />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-28 h-28 rounded-full bg-green-100"
                    style={{ animation: 'ripple 1.5s 0.3s ease-out infinite' }} />
                </div>
              </>
            )}
            <div
              className="relative inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 shadow-2xl"
              style={{
                animation: checkAnimated ? 'bounceIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' : 'none',
                transform: checkAnimated ? 'scale(1)' : 'scale(0)',
              }}
            >
              <CheckCircleIcon className="h-14 w-14 text-white drop-shadow-lg" />
              {/* Sparkle dots */}
              {['top-0 right-0', 'bottom-0 left-0', 'top-0 left-2', 'bottom-1 right-1'].map((pos, i) => (
                <span
                  key={i}
                  className={`absolute ${pos} w-3 h-3 bg-yellow-400 rounded-full`}
                  style={{ animation: `sparkle 1s ${i * 0.15}s ease-out infinite alternate` }}
                />
              ))}
            </div>
            <div
              style={{
                opacity: contentVisible ? 1 : 0,
                transform: contentVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.5s ease',
              }}
            >
              <h2 className="text-3xl font-extrabold text-gray-900 mt-4 mb-1">
                Woohoo! 🙌
              </h2>
              <p className="text-gray-500 text-base">Your service has been booked successfully</p>
            </div>
          </div>

          {/* Order ID Card */}
          <div
            className="rounded-2xl p-5 border-2 border-indigo-200 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 50%, #ecfdf5 100%)',
              opacity: contentVisible ? 1 : 0,
              transform: contentVisible ? 'translateY(0)' : 'translateY(15px)',
              transition: 'all 0.4s 0.1s ease',
            }}
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-100/50 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-semibold text-gray-600">🔖 Order ID</span>
              <span className="text-lg font-bold text-indigo-600 tracking-widest font-mono">
                {orderDetails?.orderId || `#VEGO${Date.now().toString().slice(-8)}`}
              </span>
            </div>
            <div className="h-px bg-indigo-100 mb-3" />
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-600">📋 Status</span>
              <span className="px-4 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold tracking-wide uppercase border border-amber-200"
                style={{ animation: 'pulse 2s infinite' }}>
                ⏳ Pending Confirmation
              </span>
            </div>
          </div>

          {/* Booking Details */}
          <div
            style={{
              opacity: contentVisible ? 1 : 0,
              transform: contentVisible ? 'translateY(0)' : 'translateY(15px)',
              transition: 'all 0.4s 0.2s ease',
            }}
          >
            <h4 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
              <span className="w-1 h-5 bg-indigo-500 rounded-full inline-block" />
              Booking Details
            </h4>
            <div className="grid gap-3">
              {orderDetails?.selectedDate && orderDetails?.selectedTimeSlot && (
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 hover:shadow-md transition-shadow">
                  <div className="w-11 h-11 bg-indigo-600 rounded-xl flex items-center justify-center shadow flex-shrink-0">
                    <CalendarIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Service Date & Time</p>
                    <p className="text-xs text-indigo-600 font-medium mt-0.5">
                      {orderDetails.selectedDate} at {orderDetails.selectedTimeSlot}
                    </p>
                  </div>
                </div>
              )}

              {orderDetails?.selectedStore && (
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100 hover:shadow-md transition-shadow">
                  <div className="w-11 h-11 bg-purple-600 rounded-xl flex items-center justify-center shadow flex-shrink-0">
                    <MapPinIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Service Location</p>
                    <p className="text-xs text-purple-600 font-medium mt-0.5">{orderDetails.selectedStore}</p>
                  </div>
                </div>
              )}

              {orderDetails?.bikeDetails && (
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100 hover:shadow-md transition-shadow">
                  <div className="w-11 h-11 bg-emerald-600 rounded-xl flex items-center justify-center shadow flex-shrink-0">
                    <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Vehicle</p>
                    <p className="text-xs text-emerald-600 font-medium mt-0.5">
                      {orderDetails.bikeDetails.manufacturer} {orderDetails.bikeDetails.model}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Selected Services */}
          {orderDetails?.cartItems?.length > 0 && (
            <div
              style={{
                opacity: contentVisible ? 1 : 0,
                transform: contentVisible ? 'translateY(0)' : 'translateY(15px)',
                transition: 'all 0.4s 0.3s ease',
              }}
            >
              <h4 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
                <span className="w-1 h-5 bg-purple-500 rounded-full inline-block" />
                Selected Services
              </h4>
              <div className="bg-white border-2 border-indigo-100 rounded-2xl p-4 space-y-3">
                {orderDetails.cartItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-indigo-50/30 rounded-xl"
                    style={{
                      animation: `slideInRow 0.4s ${0.1 * index}s ease-out both`,
                    }}
                  >
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded-xl shadow"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                      <p className="text-xs text-indigo-600 font-bold mt-0.5">₹{item.discountedPrice}</p>
                    </div>
                    <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info Note */}
          <div
            className="p-4 bg-blue-50 rounded-2xl border border-blue-200 flex items-start gap-3"
            style={{
              opacity: contentVisible ? 1 : 0,
              transition: 'all 0.4s 0.4s ease',
            }}
          >
            <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-blue-900 text-sm mb-1">What's next?</p>
              <p className="text-xs text-blue-700 leading-relaxed">
                You'll receive a confirmation email & SMS shortly. Our team will contact you to confirm all booking details. 📱
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div
            className="flex flex-col sm:flex-row gap-3 pt-2"
            style={{
              opacity: contentVisible ? 1 : 0,
              transform: contentVisible ? 'translateY(0)' : 'translateY(10px)',
              transition: 'all 0.4s 0.5s ease',
            }}
          >
            <button
              onClick={onClose}
              className="flex-1 px-6 py-4 bg-gray-100 text-gray-600 font-semibold rounded-2xl hover:bg-gray-200 transition-all duration-200 hover:scale-95 active:scale-90 border border-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmBooking}
              className="flex-1 px-6 py-4 text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-2xl relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)',
                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.4)',
              }}
            >
              {/* Shimmer overlay */}
              <div
                className="absolute inset-0 bg-white/20 -translate-x-full"
                style={{ animation: 'shimmer 2s infinite' }}
              />
              <CheckCircleIcon className="h-5 w-5 relative z-10" />
              <span className="relative z-10">View Service History</span>
            </button>
          </div>
        </div>
      </div>

      {/* 🎨 All Keyframe Animations */}
      <style>{`
        @keyframes popIn {
          0% { opacity: 0; transform: scale(0.7) translateY(40px); }
          60% { transform: scale(1.05) translateY(-5px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes bounceIn {
          0% { transform: scale(0) rotate(-20deg); }
          50% { transform: scale(1.2) rotate(5deg); }
          75% { transform: scale(0.95) rotate(-2deg); }
          100% { transform: scale(1) rotate(0); }
        }
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          80% { opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes ripple {
          0% { transform: scale(0.8); opacity: 0.6; }
          100% { transform: scale(1.8); opacity: 0; }
        }
        @keyframes sparkle {
          0% { transform: scale(0.5) translate(0, 0); opacity: 1; }
          100% { transform: scale(1.5) translate(4px, -4px); opacity: 0.4; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-15deg); }
          100% { transform: translateX(300%) skewX(-15deg); }
        }
        @keyframes slideInRow {
          from { opacity: 0; transform: translateX(-15px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.3); }
          50% { box-shadow: 0 0 0 8px rgba(99, 102, 241, 0); }
        }
      `}</style>
    </div>
  );
};

export default OrderConfirmationModal;
